// Hand-off action from the Studio workspace to the Freehand workspace.
//
// Exports two surfaces:
//   - `SendToFreehandButton` — pill button for the Studio topbar.
//   - `SendToFreehandCard`   — big card variant for the Export dialog.
// Both share `useSendToFreehand()` which owns the confirm-overwrite dialog
// so Studio users never silently lose their in-progress Freehand draft.

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PenLine, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import UpgradeDialog from '@/components/studio/UpgradeDialog';
import { useStudio } from '@/lib/store';
import { useFreehand } from '@/lib/freehandStore';
import { useAuth } from '@/lib/auth';

// Only shown to users who own both the Studio and Freehand Pro plans.
const useOwnsBoth = () => {
  const { isPro, isFreehandPro } = useAuth();
  return isPro && isFreehandPro;
};

export function useSendToFreehand() {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doHandoff = useCallback(() => {
    const { vertices, faces, colors, smoothVertices } = useStudio.getState();
    if (!faces || !faces.length) {
      toast.error('Nothing to send — pick a template first');
      return;
    }
    useFreehand.getState().loadFromStudio({ vertices, faces, colors, smoothVertices });
    toast.success('Sent to Freehand — keep designing');
    navigate('/draw');
  }, [navigate]);

  const trigger = useCallback(() => {
    const hasExistingDraft = (useFreehand.getState().panels || []).length > 0;
    if (hasExistingDraft) {
      setConfirmOpen(true);
    } else {
      doHandoff();
    }
  }, [doHandoff]);

  const Dialogs = (
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="send-to-freehand-confirm">
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight">Replace your Freehand work?</DialogTitle>
          <DialogDescription>
            Freehand already has panels in it. Sending this Studio design across
            will replace them. Any unsaved Freehand work will be lost — save it
            first if you want to keep it.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setConfirmOpen(false)}
            data-testid="send-to-freehand-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setConfirmOpen(false); doHandoff(); }}
            data-testid="send-to-freehand-confirm-btn"
            className="rounded-full bg-[#007AFF] hover:bg-[#0062CC] text-white"
          >
            Replace & continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { trigger, Dialogs };
}

// Compact pill for the topbar.
// Shown to single-plan owners: explains the bundle and opens the missing plan's checkout.
function LockedSendToFreehand({ className, missingPlan }) {
  const [open, setOpen] = useState(false);
  const missingName = missingPlan === 'studio' ? 'Studio Pro' : 'Freehand Pro';
  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={() => setOpen(true)}
              data-testid="btn-send-to-freehand-locked"
              className={`rounded-full h-9 px-2 sm:px-4 text-slate-500 border-dashed ${className}`}
            >
              <Lock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Send to Freehand</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[220px] text-xs" data-testid="send-to-freehand-locked-note">
            Own both Studio Pro and Freehand Pro to send designs to Freehand. Add {missingName} to unlock it.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <UpgradeDialog open={open} onOpenChange={setOpen} plan={missingPlan} hideBundle />
    </>
  );
}

export function SendToFreehandButton({ className = '' }) {
  const { trigger, Dialogs } = useSendToFreehand();
  const { isPro, isFreehandPro } = useAuth();
  if (isPro !== isFreehandPro) {
    return <LockedSendToFreehand className={className} missingPlan={isPro ? 'freehand' : 'studio'} />;
  }
  if (!isPro) return null;
  return (
    <>
      <Button
        variant="outline"
        onClick={trigger}
        data-testid="btn-send-to-freehand"
        title="Open this design in Freehand Draw"
        className={`rounded-full h-9 px-2 sm:px-4 ${className}`}
      >
        <PenLine className="w-4 h-4 sm:mr-2" />
        <span className="hidden sm:inline">Send to Freehand</span>
      </Button>
      {Dialogs}
    </>
  );
}

// Full-width card for the Export dialog.
export function SendToFreehandCard({ onBeforeHandoff }) {
  const { trigger, Dialogs } = useSendToFreehand();
  if (!useOwnsBoth()) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (onBeforeHandoff) onBeforeHandoff();
          trigger();
        }}
        data-testid="btn-send-to-freehand-card"
        className="w-full rounded-xl border border-slate-200 bg-white hover:border-[#007AFF]/40 hover:bg-[#007AFF]/5 p-3 text-left transition-colors duration-200 flex items-start gap-3"
      >
        <div className="w-9 h-9 rounded-lg bg-[#007AFF] text-white flex items-center justify-center shrink-0">
          <PenLine className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900">Send to Freehand</div>
          <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
            Open this design in the Freehand workspace and keep drawing on top —
            punch holes, redraw a panel, add a reference photo trace.
          </div>
        </div>
      </button>
      {Dialogs}
    </>
  );
}
